import { useCallback, useMemo, useState } from "react";
import cx from "classnames";
import JsonView from "@uiw/react-json-view";
import { darkTheme } from "@uiw/react-json-view/dark";
import { lightTheme } from "@uiw/react-json-view/light";

import BaseActionButton from "ds/components/BaseAction/Button";
import Button from "ds/components/Button";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import { ChevronRight } from "components/icons/generated";
import useTypedContext from "hooks/useTypedContext";
import { ThemeContext } from "views/Theme";
import Box from "ds/components/Box";
import { IconComponent } from "types/Icon";

import styles from "./styles.module.css";
import { getToolLabel, hasDisplayableData, isRunning, parseOutputData, ToolPart } from "./helpers";
import { TOOL_CALL_DESCRIPTION_FIELD } from "../constants";

const TRUNCATE_LENGTH = 60;

const JsonEntryValue = ({ value }: { value: object }) => {
  const { isDarkMode } = useTypedContext(ThemeContext);

  const filteredValue = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(value).filter(([key]) => key !== TOOL_CALL_DESCRIPTION_FIELD)
      ),
    [value]
  );

  const jsonStyle = useMemo(
    () => ({
      ...(isDarkMode ? darkTheme : lightTheme),
      "--w-rjv-background-color": "transparent",
      "--w-rjv-font-family": "var(--font-font-family-inter)",
      "--w-rjv-info-color": "var(--semantic-color-foreground-subdued)",
      "--w-rjv-color": "var(--semantic-color-foreground-subdued)",
      fontSize: "1.2rem",
      fontWeight: 400,
      lineHeight: "1.4rem",
    }),
    [isDarkMode]
  );

  return (
    <JsonView
      value={filteredValue}
      collapsed={1}
      displayObjectSize={false}
      displayDataTypes={false}
      enableClipboard={false}
      shortenTextAfterLength={TRUNCATE_LENGTH}
      style={jsonStyle}
    />
  );
};

const ToolDataSection = ({ label, data }: { label: string; data: unknown }) => {
  const isObject = typeof data === "object" && data !== null;

  return (
    <Box
      className={styles.section}
      surfaceColor="quaternary"
      padding="medium"
      direction="column"
      gap="small"
      fullWidth
    >
      <Typography tag="span" variant="p-body4" color="primary">
        {label}
      </Typography>
      {isObject ? (
        <JsonEntryValue value={data} />
      ) : (
        <Typography tag="span" variant="p-body4" color="secondary">
          {String(data)}
        </Typography>
      )}
    </Box>
  );
};

type AiChatToolCallProps = {
  className?: string;
  icon?: IconComponent;
  part: ToolPart;
  onApprove?: () => void;
  onReject?: () => void;
};

const AiChatToolCall = ({ className, icon, part, onApprove, onReject }: AiChatToolCallProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), []);

  const awaitingApproval = part.state === "approval-requested";
  const isApproved = part.state === "approval-responded" && part.approval.approved === true;
  const isRejected =
    part.state === "output-denied" ||
    (part.state === "approval-responded" && part.approval.approved === false);
  const label = getToolLabel(part);
  const errorText = part.state === "output-error" ? part.errorText : undefined;
  const input = part.state !== "input-streaming" ? part.input : undefined;
  const output = useMemo(
    () => (part.state === "output-available" ? parseOutputData(part.output) : undefined),
    [part.state, part.output]
  );

  const hasInput = hasDisplayableData(input);
  const hasOutput = hasDisplayableData(output);
  const hasContent = hasInput || hasOutput;

  return (
    <Box direction="column" className={className}>
      <BaseActionButton className={styles.header} onPress={toggleExpanded} disabled={!hasContent}>
        {icon && <Icon src={icon} color="secondary" noShrink />}
        <Typography tag="span" variant="p-body4" color="primary" className={styles.label}>
          {label}
          {isRunning(part) && "..."}
        </Typography>
        {hasContent && (
          <Icon
            src={ChevronRight}
            color="secondary"
            noShrink
            className={cx(styles.chevron, isExpanded && styles.chevronExpanded)}
          />
        )}
      </BaseActionButton>
      {errorText && (
        <Box padding="0 medium medium">
          <Typography tag="span" variant="p-body3" color="danger">
            {errorText}
          </Typography>
        </Box>
      )}
      {isApproved && (
        <Box padding="0 medium medium">
          <Typography tag="span" variant="p-body3" color="secondary">
            Approved
          </Typography>
        </Box>
      )}
      {isRejected && (
        <Box padding="0 medium medium">
          <Typography tag="span" variant="p-body3" color="secondary">
            Rejected
          </Typography>
        </Box>
      )}
      {awaitingApproval && onApprove && onReject && (
        <Box padding="0 medium medium" gap="small" align="center">
          <Button variant="primary" size="small" onPress={onApprove}>
            Approve
          </Button>
          <Button variant="secondary" size="small" onPress={onReject}>
            Reject
          </Button>
        </Box>
      )}
      {isExpanded && (
        <Box padding="0 medium medium" direction="column" gap="medium">
          {hasInput && <ToolDataSection label="Input" data={input} />}
          {hasOutput && <ToolDataSection label="Output" data={output} />}
        </Box>
      )}
    </Box>
  );
};

export default AiChatToolCall;
